import { MenuList } from '@mui/material';
import { ArrowDownLeft } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { memo, useEffect, useMemo, useState } from 'react';
import { RelatedLinkRow } from './RelatedLinkRow';
import * as S from './RelatedLinksIndicator.styles';
import type { RelatedLinkItem } from './types';

const calculateMenuHeight = (screenHeight: number, itemHeight = 53, titleHeight = 37) => {
  const maxItems = Math.floor((screenHeight * 0.625 - titleHeight) / itemHeight);
  const itemsToShow = maxItems >= 4.5 ? 4.5 : maxItems >= 3.5 ? 3.5 : 2.5;
  return itemsToShow * itemHeight + titleHeight;
};

export type RelatedLinksIndicatorProps = {
  items: readonly RelatedLinkItem[];
  /** Shown in the tooltip header. */
  menuTitle: string;
  /** Noun after the numeric count on the trigger (e.g. localized "backlinks"). */
  linksLabel: string;
  /** Icon before the count; defaults to `MoveDownLeft`. */
  triggerIcon?: ReactNode;
  /**
   * Displayed count on the trigger. Defaults to `items.length`.
   * Use when the count is sourced separately from the built menu rows.
   */
  count?: number;
  /** Label shown when there are no links. When omitted, empty state stays hidden. */
  emptyLabel?: string;
};

const RelatedLinksIndicatorComponent = ({
  items,
  menuTitle,
  linksLabel,
  triggerIcon = <ArrowDownLeft size={14} />,
  count: countProp,
  emptyLabel,
}: RelatedLinksIndicatorProps) => {
  const [menuMaxHeight, setMenuMaxHeight] = useState(0);

  useEffect(() => {
    let timeoutId: number;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        setMenuMaxHeight(calculateMenuHeight(window.innerHeight));
      }, 150);
    };

    setMenuMaxHeight(calculateMenuHeight(window.innerHeight));
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const displayCount = countProp ?? items.length;
  const showMenu = items.length > 0;
  const triggerLabel = displayCount > 0 ? `${displayCount} ${linksLabel}` : emptyLabel;

  const menuBody = useMemo(
    () =>
      items.map((item) => (
        <RelatedLinkRow
          key={item.id}
          href={item.href}
          title={item.title}
          subtitle={item.subtitle}
          icon={item.icon as ReactElement}
          status={item.status}
          date={item.date}
          startTime={item.startTime}
          disabledHint={item.disabledHint}
        />
      )),
    [items],
  );

  if (!triggerLabel) return null;

  return (
    <S.StyledTooltip
      arrow
      placement='bottom-start'
      title={
        showMenu ? (
          <S.MenuContainer maxHeight={menuMaxHeight}>
            <S.MenuTitle>{menuTitle}</S.MenuTitle>
            <MenuList dense>{menuBody}</MenuList>
          </S.MenuContainer>
        ) : null
      }
    >
      <S.TriggerRoot $interactive={showMenu} $preserveCase={displayCount <= 0}>
        {triggerIcon}
        <span>{triggerLabel}</span>
      </S.TriggerRoot>
    </S.StyledTooltip>
  );
};

export const RelatedLinksIndicator = memo(RelatedLinksIndicatorComponent);
